import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import env from './env.json'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import moment from 'moment'
// @ts-expect-error TS7016 -- MIGRATION: not approved, fix when encountered
import { MQTTClient, PageLoadingSpinner } from 'ui_kit_qmd'
import { BroadcastChannel } from 'broadcast-channel'
import { useIdentifyUser } from 'lib/segment/useIdentifyUser'
import { clear500Error, clearInternalError, setConnection } from './store/auth/AuthActions'
import { safeLocalStorage } from '@quickmd-llc/frontend-utils'
import { LOGOUT } from './store/auth/AuthConstants'
import { isInactive } from './utils/inactivity'
import Home from './pages/home/home'
import { store } from './app/store'
import { ServerError } from './pages/server-error/servererror'
import ResetPassword from './pages/reset-password/ResetPassword'
import ForgotPassword from './pages/forgot-password/ForgotPassword'
import { SecuredHIPAAPageElem } from './pages/home/SecuredHIPAAPageElem/SecuredHIPAAPageElem'
import Charts from './pages/charts/Charts'
import PatientChart from './pages/charts/patient-chart/PatientChart'
import { IntakeQuestions } from './pages/charts/patient-chart/components/intake-questions'
import { AccountSettings } from './pages/AccountSettings/AccountSettings'
import { Visits } from './pages/Visits/Visits'
import { ErrorPage } from './pages/error/ErrorPage'
import { AuthValidate } from './pages/auth-validate/AuthValidate'
import CalendarPage from './pages/Calendar/CalendarPage'
import { MedicationsPage } from './pages/Medications/MedicationsPage'
import { HomeDeliveryGuidePage } from './pages/HomeDeliveryGuide/HomeDeliveryGuidePage'
import { PharmacyOrdersPage } from './pages/PharmacyOrders/PharmacyOrdersPage'
import { ErrorBoundary, setUser as setSentryUser } from '@sentry/react'
import {
  ACCOUNT_SETTINGS_PAGE_PATH,
  AUTH_VALIDATE_PAGE_PATH,
  HOME_DELIVERY_GUIDE_PAGE_PATH,
  CALENDAR_PAGE_PATH,
  ERROR_PAGE_PATH,
  FORGOT_PASSWORD_PAGE_PATH,
  HOME_PAGE_PATH,
  MEDICATIONS_PAGE_PATH,
  NAVIGATION_TYPES,
  PATIENT_CHART_INTAKE_QUESTIONS_PAGE_PATH,
  PATIENT_CHART_PAGE_PATH,
  PATIENT_PAST_VISIT_PAGE_PATH,
  PATIENT_UPCOMING_VISIT_PAGE_PATH,
  PATIENTS_CHARTS_PAGE_PATH,
  TRACKING_PAGE_PATH,
  RESET_PASSWORD_PAGE_PATH,
  SANDBOX_PAGE_PATH,
  SERVER_ERROR_PAGE_PATH,
  SESSION_ON_STORAGE_KEY,
  VERIFY_EMAIL_PAGE_PATH,
  VISITS_PAGE_PATH,
  WAITING_ROOM_PAGE_PATH,
} from './utils/constants'
import { PatientPastVisitNotes } from './pages/charts/patient-past-visit-notes/PatientPastVisitNotes'
import { getProviderProfile, getVisitPrescriptionCollection } from './store/waitingRoom/WaitingRoomThunk'
import { useGetProfile } from 'api/generated/profile/profile'
import { clearChartPatient } from './store/charts/ChartsActions'
import { updateSchedule } from './store/calendar/CalendarActions'
import { forceUpdateWaitingRoom } from './store/waitingRoom/WaitingRoomActions'
import { appInsights } from './utils/appInsights'
import { parseTimeZone, setTabId } from './utils/common'
import CustomTabTitle from './components/customTabTitle/CustomTabTitle'
import { getDrugRulesSettings } from './store/drugRulesSettings/drugRulesSettingsThunk'
import FilePopUpContainer from './components/filePopUpContainer/FilePopUpContainer'
import { setCancelledVisitId } from './store/visits/VisitsActions'
import IDVerificationPopUpContainer from './components/id-verification-pop-up/IDVerificationPopUpContainer'
import { useSelector } from 'store/hooks/useSelector'
import { VisitCustomEvent, VisitMqttEvent } from 'pages/WaitingRoom/VisitCallRoom/hooks/useHandleVisitMqttEvents'
import { ReactQueryProvider } from 'api/components/ReactQueryProvider'
import { isDevToolsEnabled } from 'features/isDevToolsEnabled'
import { getFlags } from './store/flags/FlagsActions'
import { useUserRoles } from 'hooks/useUserRoles'
import { getDefaultHomePath, isPathAllowed, ROLES } from 'auth/roles'
import { StytchHeadlessClient } from '@stytch/vanilla-js/dist/index.headless'
import { StytchProvider } from '@stytch/react'
import { SilentErrorBoundary, toast, ToastContainer, WebAppErrorShell } from '@quickmd-llc/design-system'
import { DevToolsShell } from 'features/devTools/DevToolsShell'
import { DevToolsAppContent } from 'features/devTools/DevToolsAppContent'
import { Landing } from 'pages/WaitingRoom/Landing'
import { getAsyncVisitTopics } from 'utils/asyncVisitTopics'
import { makeBoundaryErrorReporter } from 'utils/sentryUtility'
import { SentryFeedbackWidget } from 'components/SentryFeedbackWidget'
import { IterableIdentitySync } from 'components/IterableIdentitySync'
import {
  ORDER_FULFILLMENT_DOM_EVENT,
  OrderFulfillmentMqttEvent,
  getOrderFulfillmentTopics,
} from 'pages/PharmacyOrders/fulfillment/orderFulfillmentEvents'
import { OrderFulfillmentEventsListener } from 'pages/PharmacyOrders/fulfillment/OrderFulfillmentEventsListener'

// Module scope so the handler identity is stable across renders.
const reportDevToolsAppContentError = makeBoundaryErrorReporter('dev-tools-app-content')

const selectIsLoggedIn = (state: Store) => state.auth.loggedIn

const LazySandboxPage = lazy(() => import('./pages/Sandbox/SandboxPage').then((x) => ({ default: x.SandboxPage })))

appInsights.addTelemetryInitializer((t) => t.baseType === 'ExceptionData' || t.baseType === 'EventData')

const patientChartRoutes = [PATIENT_CHART_PAGE_PATH, PATIENT_PAST_VISIT_PAGE_PATH, PATIENTS_CHARTS_PAGE_PATH]
setTabId()
const stytch = new StytchHeadlessClient(env.stytchToken)

// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
const HIPAASecurePages = (isLoggedIn, roles, defaultHomePath) => [
  {
    path: CALENDAR_PAGE_PATH,
    pageComponent:
      !isPathAllowed(CALENDAR_PAGE_PATH, roles) ?
        <Navigate to={defaultHomePath} replace />
      : <CustomTabTitle pageTitle='Calendar'>
          <CalendarPage />
        </CustomTabTitle>,
  },
  {
    path: VISITS_PAGE_PATH,
    pageComponent:
      !isPathAllowed(VISITS_PAGE_PATH, roles) ?
        <Navigate to={defaultHomePath} replace />
      : <CustomTabTitle pageTitle='Visits'>
          <Visits />
        </CustomTabTitle>,
  },
  {
    path: MEDICATIONS_PAGE_PATH,
    pageComponent:
      !isPathAllowed(MEDICATIONS_PAGE_PATH, roles) ?
        <Navigate to={defaultHomePath} replace />
      : <CustomTabTitle pageTitle='Medications'>
          <MedicationsPage />
        </CustomTabTitle>,
  },
  {
    path: TRACKING_PAGE_PATH,
    pageComponent:
      !isPathAllowed(TRACKING_PAGE_PATH, roles) ?
        <Navigate to={defaultHomePath} replace />
      : <CustomTabTitle pageTitle='Tracking'>
          <PharmacyOrdersPage />
        </CustomTabTitle>,
  },
  {
    path: SERVER_ERROR_PAGE_PATH,
    pageComponent: <ServerError />,
  },
  {
    path: HOME_DELIVERY_GUIDE_PAGE_PATH,
    pageComponent: (
      <CustomTabTitle pageTitle='How Home Delivery works'>
        <HomeDeliveryGuidePage />
      </CustomTabTitle>
    ),
  },
  {
    path: WAITING_ROOM_PAGE_PATH,
    pageComponent:
      !isPathAllowed(WAITING_ROOM_PAGE_PATH, roles) ? <Navigate to={defaultHomePath} replace /> : <Landing />,
  },
  {
    path: ACCOUNT_SETTINGS_PAGE_PATH,
    pageComponent: (
      <CustomTabTitle pageTitle='Account Settings'>
        <AccountSettings />
      </CustomTabTitle>
    ),
  },
  {
    path: PATIENTS_CHARTS_PAGE_PATH,
    pageComponent: (
      <CustomTabTitle pageTitle='Patient Charts'>
        <Charts />
      </CustomTabTitle>
    ),
  },
  {
    path: PATIENT_CHART_PAGE_PATH,
    pageComponent: <PatientChart />,
  },
  {
    path: PATIENT_CHART_INTAKE_QUESTIONS_PAGE_PATH,
    pageComponent: <IntakeQuestions />,
  },
  {
    path: HOME_PAGE_PATH,
    pageComponent: isLoggedIn ? <Navigate to={defaultHomePath} /> : <Home />,
  },
  {
    path: PATIENT_PAST_VISIT_PAGE_PATH,
    pageComponent: <PatientPastVisitNotes />,
  },
  {
    path: PATIENT_UPCOMING_VISIT_PAGE_PATH,
    pageComponent: <PatientPastVisitNotes />,
  },
  {
    path: SANDBOX_PAGE_PATH,
    pageComponent:
      isDevToolsEnabled ?
        <Suspense>
          <LazySandboxPage />
        </Suspense>
      : <Navigate to={isLoggedIn ? defaultHomePath : HOME_PAGE_PATH} />,
  },
]

const pagesWithoutHIPPARequirementsCheck = [
  {
    path: VERIFY_EMAIL_PAGE_PATH,
    pageComponent: <ResetPassword type='verifyEmail' />,
  },
  {
    path: FORGOT_PASSWORD_PAGE_PATH,
    pageComponent: (
      <CustomTabTitle pageTitle='Forgot Password'>
        <ForgotPassword />
      </CustomTabTitle>
    ),
  },
  {
    path: RESET_PASSWORD_PAGE_PATH,
    pageComponent: <ResetPassword type='resetPassword' />,
  },
  {
    path: ERROR_PAGE_PATH,
    pageComponent: <ErrorPage />,
  },
  {
    path: AUTH_VALIDATE_PAGE_PATH,
    pageComponent: <AuthValidate />,
  },
]

const perfEntries = window.performance.getEntriesByType('navigation')
const perfEntry = perfEntries[0]
// @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
const pageAccessedByReloadCheck = perfEntry.type === NAVIGATION_TYPES.RELOAD
function RouterEffects() {
  const { pathname } = useLocation()
  useEffect(() => {
    toast.dismiss('single')
  }, [pathname])
  return null
}

function SentryUserSync() {
  // Gated on the session. This component sits outside `<Routes>`, so it mounts on the
  // login page and on `/auth-validate` too — ungated it fetched the provider profile with
  // no bearer token, and that 401 is what kicked off the refresh/logout/persistor-pause
  // chain that signed providers out on every reload.
  const isLoggedIn = useSelector(selectIsLoggedIn)
  const { data: sentryProfile } = useGetProfile({ query: { enabled: isLoggedIn } })
  useEffect(() => {
    if (sentryProfile?.ProviderId) {
      setSentryUser({
        id: sentryProfile.ProviderId,
        email: sentryProfile.Email,
        username: `${sentryProfile.FirstName} ${sentryProfile.LastName}`,
      })
    } else {
      setSentryUser(null)
    }
  }, [sentryProfile?.ProviderId, sentryProfile?.Email, sentryProfile?.FirstName, sentryProfile?.LastName])
  return null
}

export default function App() {
  const dispatch = useDispatch()
  const [isLogoutLoading, setIsLogoutLoading] = useState(true)
  const [isPatientChartFlow, setIsPatientChartFlow] = useState(false)
  const [isWasVisitChangedEvent, setIsWasVisitChangedEvent] = useState(false)
  // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
  const serverErrorMessage = useSelector((state) => state.auth.serverErrorMessage)
  const accessToken = useSelector((state) => state.auth.accessToken)
  const profile = useSelector((state) => state.waitingRoomReducer.providerProfile)
  const loggedIn = useSelector((state) => state.auth.loggedIn)
  const mqttRandomClientId = useSelector((state) => state.auth.mqttRandomClientId)
  const defaultServerErrorMessage = useSelector(
    (state) =>
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      state.home.defaultServerErrorMessage ||
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      state.auth.defaultServerErrorMessage ||
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      state.waitingRoomReducer.defaultServerErrorMessage ||
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      state.calendarPageReducer.defaultServerErrorMessage ||
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      state.resetPassword.defaultServerErrorMessage ||
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      state.forgotPassword.defaultServerErrorMessage,
  )
  const providerProfile = useSelector((state) => state.waitingRoomReducer.providerProfile)
  const patientInfo = useSelector((state) => state.chartsReducer.patientInfo)
  // webWorkerSupport: false makes broadcast-channel skip its IndexedDB fallback (which crashes in
  // Safari private mode / IDB-restricted contexts with "undefined is not an object (evaluating
  // 'a.createObjectStore')") and use localstorage instead. Native BroadcastChannel is still
  // preferred when available; this only changes the fallback path. See PROVIDER-WEB-1HJ.
  const mqttBroadcastChannel = useRef(
    new BroadcastChannel('QuickMD_Provider_MQTT_Channel', { webWorkerSupport: false }),
  )
  const mqttClientRef = useRef(null)

  const roles = useUserRoles()
  const defaultHomePath = getDefaultHomePath(roles)

  useIdentifyUser()

  const VISIT_CHANGED = 'visitchanged'
  const WAITING_ROOM_CHANGED = 'waitingroomchanged'
  const PRESCRIPTION_CHANGED = 'prescriptionchanged'
  const CALENDAR_CHANGED = 'calendarchanged'
  const VISIT_CANCELLED = 'visitcancelled'
  // Async visit claim/release fan-out (from the eligible-provider async topics). NOTE the backend
  // naming mismatch: claimed is "asynchronous…" while released is "async…". Both just refresh the
  // waiting room so a claimed visit drops off (and a released one reappears) in real time.
  const ASYNC_VISIT_CLAIMED = 'asynchronousvisitclaimed'
  const ASYNC_VISIT_RELEASED = 'asyncvisitreleased'

  useEffect(() => {
    // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
    if (profile.TimeZone) {
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      moment.tz.setDefault(parseTimeZone(profile.TimeZone))
    }
  }, [profile])

  const topic = [
    `web/providers/${providerProfile?.ProviderId}`,
    ...getAsyncVisitTopics(providerProfile),
    ...getOrderFulfillmentTopics(roles),
  ]

  useEffect(() => {
    if (isWasVisitChangedEvent) {
      setTimeout(() => {
        setIsWasVisitChangedEvent(false)
      }, 150)
    }
  }, [isWasVisitChangedEvent])

  useEffect(() => {
    getFlags()
    if (
      window.location.pathname !== '/verifyemail' &&
      window.location.pathname !== '/resetPassword' &&
      window.location.pathname !== '/forgot-password' &&
      window.location.pathname !== AUTH_VALIDATE_PAGE_PATH
    ) {
      setIsLogoutLoading(true)
      const bc = new BroadcastChannel('QuickMd_Channel', { webWorkerSupport: false })
      const timeInMillseconds = moment().valueOf()

      const logoutInterval = setTimeout(() => {
        // This 500ms window exists only to ask other tabs "is one of you already open?"
        // over `QuickMd_Channel`. Silence is NOT an auth signal: it cannot distinguish
        // "no sibling tab answered" from "this is the only tab open", which is the common
        // case. It used to end the session anyway whenever `sessionStorage.sessionWillTimeout`
        // was absent — and that key was written only by a `beforeunload` handler with a
        // 10-second grace period, so every tab crash, force-quit, OS restart and reload
        // slower than 10s logged the provider out on reopen having done nothing wrong.
        // The real session gates are `auth.accessToken` (persisted; PageLayout redirects
        // when it is falsy) and `refreshSession`'s single `session-expired` LOGOUT on a
        // genuine backend rejection. All this timer does now is release the loading gate.
        setIsLogoutLoading(false)
      }, 500)
      mqttBroadcastChannel.current.onmessage = (data) => {
        if (data === 'disconnect') {
          const client = mqttClientRef.current
          if (client) {
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            client.disconnect()
          }
        }
      }
      bc.onmessage = (data) => {
        if (data === `Is opened?`) {
          const currentTime = moment().valueOf()
          if (timeInMillseconds + 150 < currentTime) {
            bc.postMessage(`Yes, already opened.`)
            window.clearTimeout(logoutInterval)
            sessionStorage.setItem(SESSION_ON_STORAGE_KEY, JSON.stringify(true))
            setIsLogoutLoading(false)
          }
        }
        if (data === `Yes, already opened.`) {
          setIsLogoutLoading(false)
          window.clearTimeout(logoutInterval)
          sessionStorage.setItem(SESSION_ON_STORAGE_KEY, JSON.stringify(true))
        }
      }
      if (!pageAccessedByReloadCheck) {
        bc.postMessage(`Is opened?`)
      } else {
        window.clearTimeout(logoutInterval)
        setIsLogoutLoading(false)
      }
    } else {
      setIsLogoutLoading(false)
    }
    // The `beforeunload` handler that wrote `sessionStorage.sessionWillTimeout` was removed
    // with its only reader (the presence logout above). Nothing else in the app reads that
    // key, so the write was pure ceremony — and a misleading one, since its name implied a
    // session control it no longer had any part in. Dropping it also dropped this effect's
    // last external dependency, so the `exhaustive-deps` suppression that used to sit here
    // is gone with it: the empty dep array is now genuinely correct rather than asserted.
  }, [])

  useEffect(() => {
    if (accessToken) {
      if (Object.keys(providerProfile || {}).length === 0) {
        dispatch(getProviderProfile())
      }
      dispatch(getDrugRulesSettings())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken])

  useEffect(() => {
    if (!isPatientChartFlow && Object.keys(patientInfo || {})?.length) {
      dispatch(clearChartPatient())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPatientChartFlow])

  const checkIsPatientChartFlow = useCallback(() => {
    if (patientChartRoutes.includes(window.location.pathname)) {
      setIsPatientChartFlow(true)
    } else {
      setIsPatientChartFlow(false)
    }
  }, [])

  useEffect(() => {
    // `safeLocalStorage.get` returns the raw string or `null`. The previous
    // `Number(safeLocalStorage.get(...))` made a missing key `0`, so this comparison was
    // always true — and because every logout path calls `safeLocalStorage.clear()`, one
    // logout guaranteed the next mount logged out again. `isInactive` treats a missing
    // timestamp as "no evidence of inactivity" and seeds it below.
    const lastUserAction = safeLocalStorage.get('lastUserAction')
    if (isInactive(lastUserAction, moment().valueOf())) {
      dispatch({ type: LOGOUT, reason: 'mount-inactivity-check' })
    } else if (!lastUserAction) {
      safeLocalStorage.set('lastUserAction', JSON.stringify(moment().valueOf()))
    }

    function changeStatus() {
      store.dispatch(setConnection(navigator.onLine))
    }
    window.addEventListener('locationchange', checkIsPatientChartFlow)
    window.addEventListener('online', changeStatus)
    window.addEventListener('offline', changeStatus)
    dispatch(clearInternalError())
    dispatch(clear500Error())

    store.dispatch(setConnection(navigator.onLine))
    return () => {
      window.removeEventListener('online', changeStatus)
      window.removeEventListener('offline', changeStatus)
      window.removeEventListener('locationchange', checkIsPatientChartFlow)
      dispatch(clearInternalError())
      dispatch(clear500Error())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (serverErrorMessage)
      appInsights.trackEvent({
        name: 'SERVER ERROR PROVIDER',
        properties: serverErrorMessage,
      })
  }, [serverErrorMessage])
  useEffect(() => {
    if (defaultServerErrorMessage)
      appInsights.trackEvent({
        name: 'SERVER ERROR PROVIDER',
        properties: defaultServerErrorMessage,
      })
  }, [defaultServerErrorMessage])

  const onMessageArrived = useCallback(
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    (message) => {
      console.log(message.payloadString)
      let payload
      try {
        payload = JSON.parse(message.payloadString)
      } catch {
        return
      }
      if (!payload || typeof payload !== 'object') return
      // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
      switch (payload.EventType) {
        case VISIT_CHANGED:
          setIsWasVisitChangedEvent(true)
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          dispatch(forceUpdateWaitingRoom(true, profile?.TimeZone))
          break
        case WAITING_ROOM_CHANGED:
          if (!isWasVisitChangedEvent) {
            // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
            dispatch(forceUpdateWaitingRoom(true, profile?.TimeZone))
          } else {
            setIsWasVisitChangedEvent(false)
          }
          break
        case PRESCRIPTION_CHANGED:
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          dispatch(getVisitPrescriptionCollection(payload?.VisitId, true))
          break
        case CALENDAR_CHANGED:
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          dispatch(updateSchedule(payload.FromDateTime, payload.ToDateTime))
          break
        case VISIT_CANCELLED: // Handle visit cancellation
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          dispatch(setCancelledVisitId(payload.VisitId))
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          dispatch(forceUpdateWaitingRoom(true, profile?.TimeZone))
          break

        case ASYNC_VISIT_CLAIMED:
        case ASYNC_VISIT_RELEASED:
          // A visit was claimed/released elsewhere — refresh so the online list reflects the pool.
          // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
          dispatch(forceUpdateWaitingRoom(true, profile?.TimeZone))
          break

        case VisitMqttEvent.VISIT_VIDEO_DECLINED:
          document.dispatchEvent(new CustomEvent(VisitCustomEvent.VISIT_VIDEO_DECLINED, { detail: payload }))
          break
        case VisitMqttEvent.VISIT_CALL_DECLINED:
          document.dispatchEvent(new CustomEvent(VisitCustomEvent.VISIT_CALL_DECLINED, { detail: payload }))
          break
        case VisitMqttEvent.VISIT_CALL_ANSWERED:
          document.dispatchEvent(new CustomEvent(VisitCustomEvent.VISIT_CALL_ANSWERED, { detail: payload }))
          break
        case OrderFulfillmentMqttEvent.FULFILLED:
        case OrderFulfillmentMqttEvent.FAILED:
          document.dispatchEvent(new CustomEvent(ORDER_FULFILLMENT_DOM_EVENT, { detail: payload }))
          break
        default:
          break
      }
    },
    // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
    [dispatch, isWasVisitChangedEvent, profile?.TimeZone],
  )

  return (
    <WebAppErrorShell
      ErrorBoundary={ErrorBoundary}
      homeHref={loggedIn ? '/waitingroom' : '/'}
      isDevToolsEnabled={isDevToolsEnabled}
      devTools={<DevToolsShell />}>
      <ReactQueryProvider>
        <SentryUserSync />
        <SentryFeedbackWidget />
        <IterableIdentitySync />
        {loggedIn && roles.includes(ROLES.PHARMACIST) ?
          <OrderFulfillmentEventsListener />
        : null}
        <div>
          {loggedIn && providerProfile?.ProviderId ?
            <MQTTClient
              isCleanSession
              onMessageArrived={onMessageArrived}
              topic={topic}
              autoConnect
              envInfo={env}
              userId={providerProfile.ProviderId}
              mqttRandomClientId={mqttRandomClientId}
              broadcastChannel={mqttBroadcastChannel.current}
              ref={mqttClientRef}
            />
          : null}
          <StytchProvider stytch={stytch}>
            {loggedIn ?
              <>
                <FilePopUpContainer />
                <IDVerificationPopUpContainer />
              </>
            : null}
            {isLogoutLoading ?
              <PageLoadingSpinner />
            : <BrowserRouter>
                {/*
                  DevToolsAppContent uses DevToolsPortal, which mounts on every route (including "/").
                  React 18's production runtime can throw "Cannot read properties of null (reading
                  'useState'/'useRef')" from renderWithHooks when the internal dispatcher is null —
                  Vite chunk-load races or a devtools browser extension probing the component. Wrap
                  in SilentErrorBoundary so a devtools crash never trips WebAppErrorShell and blanks
                  the app behind CrashScreen. Matches the fix for PROVIDER-WEB-1GY on the
                  VisitLifecycleDebugger / DashboardStateInspector DevToolsPortal call sites.

                  The boundary renders null, so it needs onError to report: DevToolsAppContent is
                  not dev-only — `getIsDevToolsEnabled(false)` is unconditionally true unless the
                  `devTools` URL param or storage key is explicitly "false" — and without a reporter
                  the swallowed crash would reach only console.error, which main.tsx stubs out on
                  the production deployment.
                */}
                <SilentErrorBoundary onError={reportDevToolsAppContentError}>
                  <DevToolsAppContent />
                </SilentErrorBoundary>
                <RouterEffects />
                <Routes>
                  {pagesWithoutHIPPARequirementsCheck.map(({ path, pageComponent }) => (
                    <Route key={path} path={path} element={pageComponent} />
                  ))}
                  {HIPAASecurePages(loggedIn, roles, defaultHomePath).map(({ path, pageComponent }) => (
                    <Route
                      key={path}
                      // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
                      exact
                      path={path}
                      element={<SecuredHIPAAPageElem pageComponent={pageComponent} />}
                    />
                  ))}
                  <Route path='*' element={<Navigate to={defaultHomePath} />} />
                </Routes>
              </BrowserRouter>
            }
          </StytchProvider>
        </div>
        <ToastContainer limit={1} icon={false} />
      </ReactQueryProvider>
    </WebAppErrorShell>
  )
}
